import { useContext, useState } from 'react'
import {
  Button,
  TextField,
  Collapse,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { UserContext } from '../../base/components/base-container/BaseContainer'
import { ROLES } from '../../global/constants/user-roles'
import { fetchData } from '../../global/utils/fetch'
import { resetUserPassword } from '../api'
import { ToastNotificationContext } from '../../global/context/toast-context/ToastNotificationContext'
import styles from './styles.module.css'
import ConfirmActionModal from './ConfirmActionModal'
import PrimaryButton from '../../global/components/buttons/primary-button/PrimaryButton'

interface AdminActionsProps {
  targetUserEmail: string
  targetUserName: string
}

export default function AdminActions({
  targetUserEmail,
  targetUserName,
}: AdminActionsProps) {
  const { user } = useContext(UserContext)
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)

  // Check if current user is Admin or Super Admin
  const isAdmin =
    user?.role?.name === ROLES.ADMIN || user?.role?.name === ROLES.SUPER_ADMIN

  // State for password reset
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordType, setPasswordType] = useState<'temporary' | 'permanent'>(
    'temporary',
  )
  const [passwordError, setPasswordError] = useState('')
  const [isResettingPassword, setIsResettingPassword] = useState(false)
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)

  if (!isAdmin) {
    return null
  }

  // Password validation
  const validatePassword = (pwd: string) => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters'
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter'
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter'
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number'
    }
    if (!/[^A-Za-z0-9]/.test(pwd)) {
      return 'Password must contain at least one special character'
    }
    return ''
  }

  const handlePasswordChange = (value: string) => {
    setPassword(value)
    setPasswordError(validatePassword(value))
  }

  const handleResetPassword = () => {
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    const validationError = validatePassword(password)
    if (validationError) {
      setPasswordError(validationError)
      return
    }

    setShowPasswordConfirm(true)
  }

  const confirmResetPassword = async () => {
    try {
      setIsResettingPassword(true)
      await fetchData(
        resetUserPassword(targetUserEmail, {
          temporaryPassword: passwordType === 'temporary' ? password : undefined,
          permanentPassword: passwordType === 'permanent' ? password : undefined,
        }),
      )
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message:
          passwordType === 'temporary'
            ? `Password reset successfully for ${targetUserName}. User will be required to change it on next login.`
            : `Password set successfully for ${targetUserName}.`,
        severity: 'success',
      })
      setShowPasswordReset(false)
      setPassword('')
      setConfirmPassword('')
      setShowPasswordConfirm(false)
    } catch (err: any) {
      console.error(err)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: `Failed to reset password: ${err.message}`,
        severity: 'error',
      })
    } finally {
      setIsResettingPassword(false)
    }
  }

  return (
    <div className={styles.adminActionsContainer}>
      <div className={styles.adminActionsTitle}>Admin Actions</div>

      {/* Reset Password Section */}
      <div className={styles.adminActionSection}>
        <Button
          variant='outlined'
          color='error'
          onClick={() => setShowPasswordReset(!showPasswordReset)}
          fullWidth
        >
          Reset Password
        </Button>
        <Collapse in={showPasswordReset}>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <RadioGroup
              row
              value={passwordType}
              onChange={(e) =>
                setPasswordType(e.target.value as 'temporary' | 'permanent')
              }
            >
              <FormControlLabel
                value='temporary'
                control={<Radio size='small' />}
                label='Temporary (user must change on login)'
              />
              <FormControlLabel
                value='permanent'
                control={<Radio size='small' />}
                label='Permanent'
              />
            </RadioGroup>
            <TextField
              type='password'
              label='New Password'
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              error={!!passwordError}
              helperText={passwordError}
              fullWidth
              size='small'
            />
            <TextField
              type='password'
              label='Confirm Password'
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={
                password !== confirmPassword && confirmPassword.length > 0
              }
              helperText={
                password !== confirmPassword && confirmPassword.length > 0
                  ? 'Passwords do not match'
                  : ''
              }
              fullWidth
              size='small'
            />
            <PrimaryButton
              buttonName={isResettingPassword ? 'Resetting...' : 'Set Password'}
              onClick={handleResetPassword}
              disabled={
                isResettingPassword ||
                !!passwordError ||
                password !== confirmPassword ||
                !password
              }
            />
          </Box>
        </Collapse>
      </div>

      {/* Confirmation Modal */}
      <ConfirmActionModal
        isOpen={showPasswordConfirm}
        title='Confirm Password Reset'
        message={`Are you sure you want to ${passwordType === 'temporary' ? 'set a temporary' : 'set a permanent'} password for ${targetUserName} (${targetUserEmail})?${passwordType === 'temporary' ? ' They will be required to change their password on next login.' : ''}`}
        confirmLabel='Reset Password'
        onConfirm={confirmResetPassword}
        onCancel={() => setShowPasswordConfirm(false)}
        isLoading={isResettingPassword}
        severity='warning'
      />
    </div>
  )
}
